import React, { useEffect, useState, useContext } from 'react';
import { bool, number, string } from 'prop-types';
import { Link } from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Info } from '~/@one-thd/sui-icons';
import { useContentfulContext } from '../../../providers/ContentfulContext';
import { clickEvent, loadEvent } from '../../utils/publisher';
import { getDefaultApplyNowURL } from '../../utils/plcc-promo-info';
import { PLCN_CARD_IMAGE_SMALL, CARD_IMAGE } from '../../utils/constants';
import { formatNumber, getDynamicOffer } from '../../utils/general';
import TermsAndConditionsDrawer from '../../common/terms-and-conditions-drawer.component';
import { TermsAndConditionsDrawerContentful }
from '../../common/terms-and-conditions-drawer-contentful.component';

export const IntroOffer = ({
  campaignCode, mobile, price, isB2B, showGenericDiscount, isPlp
}) => {
  const { plccData } = useContentfulContext();
  const [applyNowUrl, setApplyNowUrl] = useState(null);
  const { isConsumerApp } = useContext(ExperienceContext);
  const [showDrawer, setShowDrawer] = useState(false);

  const openDrawer = () => {
    setShowDrawer(true);
  };

  const closeDrawer = () => {
    setShowDrawer(false);
  };

  const applyText = isB2B
    ? 'Apply for a Pro Xtra Credit Card'
    : 'Apply for a Home Depot Consumer Card';
  const cardIcon = isB2B ? CARD_IMAGE.PLCR_HOMEDEPOT : PLCN_CARD_IMAGE_SMALL;

  const logLoadEventAnalytics = () => {
    if (isB2B && price > 298.99) {
      loadEvent('plcc 299+ disc');
    } else if (price > 24.99 && price < 850.00) {
      loadEvent('plcc 25-850 disc');
    } else if (plccData) {
      loadEvent('plcc');
    }
  };

  const logClickEventAnalytics = () => {
    if (isB2B && price > 298.99) {
      clickEvent('plcc 299+ disc');
    } else if (price > 24.99 && price < 850.00) {
      clickEvent('plcc 25-850 disc');
    } else if (plccData) {
      clickEvent('plcc');
    }
  };

  const applyNowClick = () => {
    logClickEventAnalytics();
    return isConsumerApp ? window.open(applyNowUrl, 'self') : window.open(applyNowUrl, '_blank');
  };

  useEffect(() => {
    if (plccData) {
      setApplyNowUrl(getDefaultApplyNowURL(plccData, campaignCode, mobile, isB2B));
      logLoadEventAnalytics();
    }
  }, [plccData]);

  const calculateIntroOfferDiscountPrice = (amount) => {
    return formatNumber((amount - getDynamicOffer(amount)).toFixed(2));
  };

  const handlePlpText = (isPlpExp) => {
    if (isPlpExp) {
      return (
        <>
          <span className="sui-text-sm sui-font-light">
            Pay&nbsp;
            <strong>{`$${calculateIntroOfferDiscountPrice(price)}`}</strong>
            &nbsp;after&nbsp;
            <strong>{`$${getDynamicOffer(price)} off`}</strong>
            &nbsp;a qualifying purchase.
            <span className="sui-cursor-pointer sui-relative sui-down-1">
              <Info
                aria-label="Credit Offer Details"
                color="info"
                onClick={openDrawer}
                size="small"
              />
            </span>
          </span>
        </>
      );
    }
    return (
      <>
        Pay<strong>{` $${calculateIntroOfferDiscountPrice(price)}`}</strong>
        {'\xA0after '}<strong>{`$${getDynamicOffer(price)} OFF`}</strong>
        {' your total qualifying purchase upon opening a new card.\xA0'}
        <span className="sui-cursor-pointer sui-relative sui-down-1">
          <Info
            aria-label="Credit Offer Details"
            color="info"
            onClick={openDrawer}
            size="small"
          />
        </span>
      </>
    );
  };

  return (
    <>
      {
        isPlp ? (
          <TermsAndConditionsDrawer
            closeDrawer={closeDrawer}
            open={showDrawer}
            isB2B={isB2B}
            price={price}
            isPlp={isPlp}
          />
        ) : (
          <TermsAndConditionsDrawerContentful
            closeDrawer={closeDrawer}
            open={showDrawer}
            isB2B={isB2B}
            price={price}
            campaignCode={campaignCode}
            isPlp={isPlp}
          />
        )
      }
      <div className="sui-inline-flex sui-items-center">
        <img
          src={cardIcon}
          alt="Apply Now"
          className="sui-pr-2"
          width="50"
          height="50"
        />
        <div>
          <div>
            {showGenericDiscount
              ? <><strong>Save up to $100</strong>{' on your qualifying purchase'}<>&nbsp;</></>
              : (
                handlePlpText(isPlp)
              )}
          </div>
          {!isPlp
            && (
              <Link
                onClick={applyNowClick}
                color="brand"
                variant="body-sm"
              >
                {applyText}
              </Link>
            )}
        </div>
      </div>
    </>
  );
};

IntroOffer.displayName = 'IntroOffer';

IntroOffer.propTypes = {
  campaignCode: string.isRequired,
  mobile: bool.isRequired,
  price: number.isRequired,
  isB2B: bool.isRequired,
  showGenericDiscount: bool.isRequired,
  isPlp: bool
};

IntroOffer.defaultProps = {
  isPlp: false
};
